import { AnimatePresence, motion } from 'framer-motion';
import React, { useCallback, useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';
import styled from 'styled-components';

import { MODAL_ANIMATION, MODAL_Z_INDEX, PANEL_WIDTH } from './constants';
import { UnifiedModalContext } from './context';
import { ModalContextValue, ModalPanelProps } from './types';

const Overlay = styled(motion.div)`
  position: fixed;
  inset: 0;
  z-index: ${MODAL_Z_INDEX.panelOverlay};
  background: rgba(0, 0, 0, 0.6);
`;

const PanelContainer = styled(motion.div)<{ $slideFrom: string; $height: string }>`
  position: fixed;
  z-index: ${MODAL_Z_INDEX.panel};
  background: ${({ theme }) => theme.modal.contentBackgroundColor};
  display: flex;
  flex-direction: column;
  width: ${PANEL_WIDTH.width};
  min-width: ${PANEL_WIDTH.minWidth};
  max-width: ${PANEL_WIDTH.maxWidth};
  box-shadow: -8px 0 32px rgba(0, 0, 0, 0.4);

  ${({ $slideFrom }) => {
    switch ($slideFrom) {
      case 'left':
        return 'top: 0; left: 0; bottom: 0;';
      case 'right':
        return 'top: 0; right: 0; bottom: 0;';
      case 'top':
        return 'top: 0; left: 0; right: 0;';
      case 'bottom':
        return 'bottom: 0; left: 0; right: 0;';
      default:
        return 'top: 0; right: 0; bottom: 0;';
    }
  }}

  ${({ $height }) => {
    switch ($height) {
      case 'full':
        return 'height: 100vh;';
      case 'fixed':
        return 'height: 70vh;';
      default:
        return '';
    }
  }}
`;

export const ModalPanel: React.FC<ModalPanelProps> = ({
  open,
  onClose,
  onCancel,
  onConfirm,
  slideFrom = 'right',
  loading = false,
  hideCloseButton = false,
  closeOnEsc = true,
  height = 'full',
  className,
  children,
}) => {
  const dismiss = useCallback(() => {
    if (loading) return;
    (onCancel ?? onClose)?.();
  }, [onCancel, onClose, loading]);

  useEffect(() => {
    if (!open || !closeOnEsc) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        dismiss();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, closeOnEsc, dismiss]);

  const contextValue = useMemo<ModalContextValue>(
    () => ({
      dismiss,
      onConfirm,
      closeOnEsc,
      closeOnOverlayClick: false,
      loading,
      hideCloseButton,
    }),
    [dismiss, onConfirm, closeOnEsc, loading, hideCloseButton],
  );

  const initialAnim = MODAL_ANIMATION.panel.initial(slideFrom);
  const exitAnim = MODAL_ANIMATION.panel.exit(slideFrom);

  return ReactDOM.createPortal(
    <UnifiedModalContext.Provider value={contextValue}>
      <AnimatePresence>
        {open && (
          <>
            <Overlay
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: MODAL_ANIMATION.duration }}
              onClick={dismiss}
            />
            <PanelContainer
              $slideFrom={slideFrom}
              $height={height}
              className={className}
              initial={initialAnim}
              animate={MODAL_ANIMATION.panel.animate}
              exit={exitAnim}
              transition={{ duration: MODAL_ANIMATION.duration, ease: 'easeInOut' }}
            >
              {children}
            </PanelContainer>
          </>
        )}
      </AnimatePresence>
    </UnifiedModalContext.Provider>,
    document.body,
  );
};
